import type {
  IProductRepository,
  ProductFilters,
} from '../../domain/repositories/IProductRepository';
import {
  ApiCategoriesResponse,
  ApiFiltersResponse,
  ApiHomepageCollections,
  ApiProductResponse,
  PaginatedResponse,
  Product,
  ProductCreateParams,
  ProductPaymentOptionsResponse,
  ProductPaymentPlanResponse,
  ProductStatus,
  ProductUpdateParams,
} from '../../domain/entities/Product';
import type { AuthTokenService } from '../services/AuthTokenService';
import axios from 'axios';

export class ApiProductRepository implements IProductRepository {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.authTokenService.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newToken = await this.authTokenService.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.apiClient(originalRequest);
          } catch (refreshError) {
            this.authTokenService.clearAuthToken();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async create(product: ProductCreateParams): Promise<Product> {
    const response = await this.apiClient.post(
      '/v1/products',
      this.mapToApiFormat(product)
    );
    return this.mapFromApiResponse(response.data) as Product;
  }

  async findById(id: string): Promise<Product | null> {
    try {
      const response = await this.apiClient.get(`/v1/products/${id}`);
      return response.data as Product; //this.mapFromApiResponse(response.data) as Product;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async findByIds(ids: string[]): Promise<Product[]> {
    const response = await this.apiClient.get('/v1/products', {
      params: { ids: ids.join(',') },
    });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findAll(filters?: ProductFilters): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findByCategory(
    categoryId: string,
    filters?: ProductFilters
  ): Promise<PaginatedResponse<Product>> {
    const params = this.buildQueryParams({
      ...filters,
      categories: [categoryId],
    });
    const response = await this.apiClient.get('/v1/products', { params });
    const mapProducts = response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );

    return {
      ...response,
      results: mapProducts,
    };
  }

  async findByShop(
    shopId: string,
    filters?: ProductFilters
  ): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    params.merchant_shop_id = shopId;
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findFeatured(limit: number = 10): Promise<Product[]> {
    const response = await this.apiClient.get('/v1/products', {
      params: { featured: true, limit },
    });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async search(query: string, filters?: ProductFilters): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    params.q = query;
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async update(id: string, updates: ProductUpdateParams): Promise<Product> {
    const response = await this.apiClient.patch(
      `/v1/products/${id}`,
      this.mapToApiFormat(updates)
    );
    return this.mapFromApiResponse(response.data) as Product;
  }

  async delete(id: string): Promise<void> {
    await this.apiClient.delete(`/v1/products/${id}`);
  }

  async exists(id: string): Promise<boolean> {
    try {
      await this.apiClient.head(`/v1/products/${id}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // New methods for API-specific functionality
  async findCollections(): Promise<any> {
    const response = await this.apiClient.get(
      '/v1/products/collections/homepage'
    );
    return response.data.sections;
  }

  async findFeaturedCategories(limit?: number): Promise<ApiCategoriesResponse> {
    const params = limit ? { limit } : {};
    const response = await this.apiClient.get(
      '/v1/products/categories/featured',
      { params }
    );
    return response.data;
  }

  async findNavigationCategories(): Promise<ApiCategoriesResponse> {
    const response = await this.apiClient.get(
      '/v1/products/categories/navigation'
    );
    return response.data;
  }

  async findRelated(productId: string, limit: number = 5): Promise<Product[]> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/related`,
      {
        params: { limit },
      }
    );
    return response.data.related_products.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async getPaymentOptions(
    productId: string
  ): Promise<ProductPaymentOptionsResponse> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/payment-options`
    );
    return response.data;
  }

  async getPaymentPlans(
    productId: string
  ): Promise<ProductPaymentPlanResponse> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/payment-plans`
    );
    return response.data;
  }

  async getProductFilters(): Promise<ApiFiltersResponse> {
    const response = await this.apiClient.get('/v1/products/filters');
    return response.data;
  }

  async getCategoryNavigation(): Promise<ApiCategoriesResponse> {
    const response = await this.apiClient.get(
      '/v1/products/categories/navigation'
    );
    return response.data;
  }

  async getCategoryHierarchy(
    includeProductCount: boolean = true
  ): Promise<ApiCategoriesResponse> {
    const params = includeProductCount ? { include_product_count: 'true' } : {};
    const response = await this.apiClient.get(
      '/v1/products/categories/hierarchy',
      { params }
    );
    return response.data;
  }

  async getFeaturedCategories(
    isFeatured: boolean = true
  ): Promise<ApiCategoriesResponse> {
    const params = isFeatured ? { featured: 'true' } : { limit: 100 };
    const response = await this.apiClient.get('/v1/products/categories', {
      params,
    });
    return response.data;
  }

  async getHomepageCollections(): Promise<ApiHomepageCollections> {
    const response = await this.apiClient.get(
      '/v1/products/collections/homepage'
    );
    return response.data;
  }

  private buildQueryParams(filters?: ProductFilters): any {
    if (!filters) return {};

    const params: any = {};

    if (filters.categories?.length) {
      params.category = filters.categories.join(',');
    }
    if (filters.brandName) {
      params.brand = filters.brandName;
    }
    if (filters.minPrice !== undefined) {
      params.price_min = filters.minPrice;
    }
    if (filters.maxPrice !== undefined) {
      params.price_max = filters.maxPrice;
    }
    if (filters.isFeatured !== undefined) {
      params.featured = filters.isFeatured;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.limit) {
      params.limit = filters.limit;
    }
    if (filters.offset) {
      params.offset = filters.offset;
    }
    if (filters.sortBy && filters.sortOrder) {
      params.sort = `${filters.sortBy}_${filters.sortOrder}`;
    }

    return params;
  }

  private mapFromApiResponse(apiData: ApiProductResponse): Product {
    return Product.fromObject(apiData as any) as Product;
  }

  private mapToApiFormat(
    productData: Partial<ProductCreateParams | ProductUpdateParams>
  ): any {
    return {
      name: productData.name,
      brand_name: productData.brand_name,
      model_no: productData.model_no,
      description: productData.description,
      rich_description: productData.rich_description,
      cost_price: productData.cost_price,
      retail_price: productData.retail_price,
      max_payment_period: productData.max_payment_period,
      images: productData.images || [],
      categories: productData.categories,
      merchant_shop_id: productData.merchant_shop_id,
      shop_name: productData.shop_name,
      shop_logo: productData.shop_logo,
      lender_id: productData.lender_id,
      is_featured: productData.is_featured,
      status: productData.status,
    };
  }

  private mapApiStatusToDomain(apiStatus: string): ProductStatus {
    switch (apiStatus) {
      case 'in_stock':
      case 'A':
        return ProductStatus.ACTIVE;
      case 'out_of_stock':
      case 'O':
        return ProductStatus.OUT_OF_STOCK;
      case 'discontinued':
      case 'D':
        return ProductStatus.DISCONTINUED;
      default:
        return ProductStatus.INACTIVE;
    }
  }
}
